import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Spin } from 'antd';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  invoices: PropTypes.array,
};

const defaultProps = {
  title: '',
  invoices: [],
};

@autobind
class TableInvoices extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      exportingInvoiceId: null,
      exportingSelectedInvoices: false,
    };
  }

  async onPrintInvoiceClick(id) {
    try {
      this.setState({
        exportingInvoiceId: id,
      });

      const params = { ids: id };
      const responseBody = await this.callService('/invoice/export', params);

      // Open a window to show pdf.
      const blob = helpers.base64toBlob(responseBody.getRetBlob(), 'application/pdf');
      window.open(URL.createObjectURL(blob));
    } finally {
      this.setState({
        exportingInvoiceId: null,
      });
    }
  }

  async onPrintSelectedInvoicesClick() {
    const selectedInvoiceIds = this.dataTables.getSelectedRowKeys();
    if (selectedInvoiceIds.length > 0) {
      try {
        this.setState({
          exportingSelectedInvoices: true,
        });

        const params = { ids: selectedInvoiceIds };
        const responseBody = await this.callService('/invoice/export', params);

        // Open an window to show pdf.
        const blob = helpers.base64toBlob(responseBody.getRetBlob(), 'application/pdf');
        window.open(URL.createObjectURL(blob));
      } finally {
        this.setState({
          exportingSelectedInvoices: false,
        });
      }
    }
  }

  createColumns() {
    const { exportingInvoiceId } = this.state;

    return [{
      title: 'Invoice Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.id}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Auth. Code',
      dataIndex: 'authorizationCode',
      sorter: (a, b) => a.authorizationCode?.localeCompare(b.authorizationCode),
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Visits From',
      dataIndex: 'serviceStartDateUsString',
      sorter: (a, b) => a.serviceStartDate - b.serviceStartDate,
    }, {
      title: 'Visits To',
      dataIndex: 'serviceEndDateUsString',
      sorter: (a, b) => a.serviceEndDate - b.serviceEndDate,
    }, {
      title: 'Mail Date',
      dataIndex: 'mailDateUsString',
      sorter: (a, b) => a.mailDate - b.mailDate,
    }, {
      title: 'Total Visits',
      dataIndex: 'totalVisits',
      sorter: (a, b) => a.totalVisits - b.totalVisits,
      exportAsNumber: true,
    }, {
      title: 'Total Hours',
      dataIndex: 'totalHoursString',
      sorter: (a, b) => a.totalHours - b.totalHours,
    }, {
      title: 'Total Amt.',
      dataIndex: 'amountInDollars',
      sorter: (a, b) => a.amount - b.amount,
    }, {
      title: 'Last Export',
      dataIndex: 'lastExportTimeUsString',
      sorter: (a, b) => a.lastExportTime - b.lastExportTime,
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          disabled={exportingInvoiceId === record.id}
          onClick={() => this.onPrintInvoiceClick(record.id)}
        >
          {exportingInvoiceId === record.id &&
            <Spin />
          }

          {exportingInvoiceId !== record.id &&
            'PRINT'
          }
        </CustomButton>
      ),
    }];
  }

  createDataSource(invoices) {
    const dataSource = [];
    invoices.forEach((invoice, index) => {
      dataSource.push({
        key: invoice.getId(),
        id: invoice.getId(),
        invoiceDate: invoice.getLong('invoiceDate'),
        invoiceDateUsString: invoice.getString('invoiceDateUsString'),
        invoiceNumber: invoice.getString('invoiceNumber'),
        authorizationCode: invoice.getString('authorizationCode'),
        companyName: invoice.getString('companyName'),
        patientId: invoice.getLong('patientId'),
        patientName: invoice.getString('patientName'),
        serviceStartDate: invoice.getLong('serviceStartDate'),
        serviceStartDateUsString: invoice.getString('serviceStartDateUsString'),
        serviceEndDate: invoice.getLong('serviceEndDate'),
        serviceEndDateUsString: invoice.getString('serviceEndDateUsString'),
        mailDate: invoice.getLong('mailDate'),
        mailDateUsString: invoice.getString('mailDateUsString'),
        totalVisits: invoice.getLong('totalVisits'),
        totalHours: invoice.getLong('totalHours'),
        totalHoursString: invoice.getString('totalHoursString'),
        amount: invoice.getDouble('amount'),
        amountInDollars: helpers.toDollars(invoice.getDouble('amount')),
        lastExportTime: invoice.getLong('lastExportTime'),
        lastExportTimeUsString: invoice.getString('lastExportTimeUsString'),
        remark: invoice.getString('remark'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable
        columnKeysToAlign={['totalVisits', 'totalHoursString', 'amountInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('totalVisits'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('amount')]
        }
        formatters={[
          n => n,
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderFooter() {
    const { exportingSelectedInvoices } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          loading={exportingSelectedInvoices}
          onClick={this.onPrintSelectedInvoicesClick}
        >
          PRINT SELECTED INVOICES
        </CustomButton>
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { invoices, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-text"
        selectable
        columns={this.createColumns()}
        dataSource={this.createDataSource(invoices)}
        tfoot={this.renderTfoot}
        footer={this.renderFooter()}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableInvoices.propTypes = propTypes;
TableInvoices.defaultProps = defaultProps;

export default TableInvoices;
